<script setup lang="ts">
import { ref } from "vue";
import { Permission } from "../../../../types/auth";
import SalesInvoicesForm from "./SalesInvoicesForm.vue";
import { RecordStatus } from "../../../../types/models";
import ReceivePayment from "../../../finance/paymentreceipts/paymentreceipts/ReceivePayment.vue";
import { FilterField, TableAction, TableField } from "../../../../components/table/table";
import ListTemplate from "../../../../components/ui/common/ListTemplate.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import SalesInvoicesImportExcel from "./SalesInvoicesImportExcel.vue";
import MobileSalesView from "../mobilesales/MobileSalesView.vue";
import { useScreenSize } from "../../../../utils/useScreenSize";
import { api } from "../../../../api/Api";
import MessageBox from "../../../../services/MessageBox";
import { printPDFBlob } from "../../../../utils/CommonFunctions";

const { isMobile } = useScreenSize();

const endPoint = "/clients/revenue/sales/sales-invoices/";
const title = "Sales Invoices";
const selectedData = ref<any | null>(null);
const showForm = ref(false);
const formMode = ref<
  "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
>("view");
const refreshData = ref(false);
const showReceivePayment = ref(false)
const permissions = ref<Permission[]>();

const showImportExcel = ref(false);

const fields: TableField[] = [
  { name: "date_time", caption: "Date", sortable: true, fieldType: "date" },
  { name: "due_date", caption: "Due Date", sortable: true, fieldType: "date" },
  {
    name: "document_no",
    caption: "Ref. No",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "warehouse",
    caption: "Warehouse",
    sortable: true,
    fieldType: "text",
  },
  { name: "outlet", caption: "Outlet", sortable: true, fieldType: "text" },
  { name: "customer", caption: "customer", sortable: true, fieldType: "text" },
  { name: "net_total", caption: "Amount", sortable: true, fieldType: "number" },
  {
    name: "due_amount",
    caption: "Due Amount",
    sortable: true,
    fieldType: "number",
  },
  {
    name: "sales_channel",
    caption: "Channel",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "delivery_date",
    caption: "Delivery By",
    sortable: true,
    fieldType: "date",
  },
  {
    name: "credit_term",
    caption: "Payment Terms",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "sales_person",
    caption: "Sales Rep",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "customer_reference",
    caption: "customer Reference",
    sortable: true,
    fieldType: "text",
  },
  {
    name: "to_deliver",
    caption: "To Deliver",
    sortable: false,
    fieldType: "boolean",
  },
  {
    name: "record_status",
    caption: "Status",
    sortable: false,
    fieldType: "text",
  },
];
const actions: TableAction[] = [
  {
    name: "receive-payment",
    icon: "Banknote",
    variant: "primary",
    title: "Receive Payments",
    permission: "Receive Payments",
    dropdown: true,
    visible: (item) => item.due_amount > 0 && item.status == 'Approved',
  },
  {
    name: "create-credit-note",
    icon: "WalletCards",
    variant: "primary",
    title: "Create Credit Note",
    permission: "Create Credit Note",
    dropdown: true,
    visible: (item) => item.record_status == 'Approved',
  },
];

const filterFields: FilterField[] = [
  {
    name: "active",
    label: "Status",
    type: "select",
    value: "",
    options: [
      { id: "1", name: "Active" },
      { id: "0", name: "Inactive" },
    ],
  },
];

const changeMode = (
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus"
) => {
  console.log('changeMode', mode)
  formMode.value = mode;
  showForm.value = mode === "view" || mode === "edit" || mode === "create";
};

const handleData = (data: any) => {
  selectedData.value = data;
};

const record_status = ref<RecordStatus[]>([]);

const handleLists = (list: any) => {
  record_status.value = list["record_status"];
};

const handleTableActions = (action: any, data: any)  => {
  selectedData.value = data
  if (action == 'receive-payment') {
    showReceivePayment.value = true
  } else if (action == 'create-credit-note') {
    createCreditNote(data);
  }
}

const handlePermissions = (perm: Permission[]) => {
  permissions.value = perm;
};

const listViewRef = ref<InstanceType<typeof ListTemplate> | null>(null);
const viewDocument = (data : any, title: string, path: string) => {
  if (listViewRef.value) {
    listViewRef.value.viewDocument(data, title, path);
  }
}

const createCreditNote = async (record: any) => {
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") +
      "convert",
    {
      id: record.uid,
      convert: 'Credit Note',
    }
  );
  if (response.success == true) {
    refreshData.value = !refreshData.value
    MessageBox.confirm({
      title: "Credit Note Created",
      message:
        "Credit Note created successfully",
      confirmText: "Print Credit Note",
      cancelText: "Close",
    }).then((confirmed) => {
      if (confirmed) {
        printPDFBlob(response.data.file);
      }
    });
  }
};

</script>

<template>
  <ListTemplate
   ref="listViewRef"
    :endPoint="endPoint"
    :title="title"
    :fields="fields"
    :actions="actions"
    :filterFields="filterFields"
    @permissions="handlePermissions"
    :tabList="[]"
    :refreshData="refreshData"
    @custom="handleTableActions"
    @mode="changeMode"
    @show="showForm = true"
    :showForm="showForm"
    @lists="handleLists"
    :allow-disable="false"
    :show-quick-view="true"
    :show-share-actions="true"
    :allow-edit="false"
    @data="handleData"
  >
  <template #header-actions>
    <Buttons
      caption="Import Sales using Excel Files"
      icon="Import"
      @click="showImportExcel = true"/>
    </template>
    <template #form-view>
      <SalesInvoicesForm
        v-if="showForm && !isMobile"
        :permissions="permissions"
        :record-status="record_status"
        :data="selectedData"
        :end-point="endPoint"
        @view-document="viewDocument"
        :title="title"
        :mode="formMode"        
        @save="refreshData = !refreshData"
        @close="showForm = !showForm"
      />
      <Teleport to="body">
    <div
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0"
      v-if="showForm && isMobile"
    >
        <MobileSalesView />
      </div>
      </Teleport>
    </template>
  </ListTemplate>
  <ReceivePayment v-if="showReceivePayment" :data="selectedData" @close="refreshData = !refreshData; showReceivePayment = false"/>
  <SalesInvoicesImportExcel
    :endPoint="endPoint"
      v-if="showImportExcel"
      @close="showImportExcel = false"
    />

</template>
